/* ==========================================================================
   Focus discipline — one implementation, every dialog.

   THE PROBLEM THIS SOLVES
   Measured on this build before it existed: open ⌘K, press Tab four times, and
   focus is on the sidebar behind the scrim while the palette is still up.
   Press Esc there and nothing closes, because the palette's Escape handler is
   bound to its input. Register a robot and the form has no Escape handler at
   all. Close any of them and focus is wherever it wandered to. Nine dialogs,
   nine different half-answers.

   Rather than nine fixes, one document-level rule, which every dialog in the
   build gets for free the moment it renders `role="dialog" aria-modal="true"`:

     1  ENTER   focus moves into the dialog if it is not already there.
     2  TRAP    everything outside the dialog's own ancestor chain is marked
                `inert`, so Tab physically cannot leave and a stray click
                cannot reach the surface behind. Wrap-around is explicit so the
                cycle never escapes through the browser chrome either.
     3  ESCAPE  Esc anywhere inside a modal dismisses it. The dialog's own
                handler gets first refusal; if the dialog is still standing a
                frame later, Escape does exactly what clicking the scrim does —
                every modal in this build dismisses on its overlay — and if
                there is no scrim, it presses the dialog's own Cancel/Close.
     4  RESTORE on close, focus returns to whatever opened the dialog. If that
                element has gone with the dialog, focus lands on <main> rather
                than on <body>, so the next Tab resumes in the content instead
                of at the top of the document.

   Plus one guard that has nothing to do with dialogs: when the focused element
   is removed from the document — a row that a poll dropped, a panel that
   collapsed — focus silently falls to <body> and the keyboard goes dead. The
   guardian below catches that and puts the cursor back on <main>.

   `aria-modal="true"` is the contract. A non-modal `role="dialog"` (the fleet
   map's detail card, the filter popovers) is deliberately untouched: those are
   panes, and trapping focus in a pane would be the defect.
   ========================================================================== */

const MODAL_SELECTOR = '[role="dialog"][aria-modal="true"]';

/* `:not([tabindex="-1"])` on EVERY entry, not just the generic one. A roving
   tabindex (widgets.ts) parks `tabindex="-1"` on the five segments of a
   segmented control that are not the current stop; without the guard those
   five still counted as tab stops here, so a dialog holding one computed its
   "last focusable" as a control the Tab key can never reach and the wrap-around
   jumped to the wrong end. */
const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]:not([tabindex="-1"])',
  'video[controls]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"]):not([tabindex="-1"])',
].join(',');

/** The console layer's own root. Never inerted: the live regions and the
    acknowledgement toasts have to keep speaking while a modal is up. */
const LAYER_ATTR = 'data-f3-layer';

function isVisible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/* The last one in document order. Cheap on purpose: the observer below runs
   it every frame the DOM changes, and a getBoundingClientRect in here would
   cost a forced layout per frame on the map, which mutates constantly. */
function topModal(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(MODAL_SELECTOR);
  return all.length === 0 ? null : (all[all.length - 1] ?? null);
}

/** Every control inside `root` the Tab key can actually reach, in tab order.
    Shared with widgets.ts so the popover trap and the modal trap agree on what
    a tab stop is. */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => isVisible(el) && el.closest('[inert]') === null,
  );
}

/* --------------------------------------------------------------------------
   The steered cursor

   `:focus-visible` is a heuristic about how the OPERATOR last interacted, and
   it gets this case wrong. Open the Columns popover with the mouse and the
   console moves the keyboard into the panel — a deliberate, invisible-by-
   default move, because the browser's last input was a click and it therefore
   declines to draw a ring. The operator is now standing somewhere with no mark
   on the screen saying so.

   So: anything THIS layer moves the cursor to is marked, and global.css draws
   the console's one focus ring on it whether the heuristic agrees or not. The
   mark is dropped the moment focus leaves, so it never becomes a second,
   stickier kind of selection. Same argument, same ring, as the `j`/`k` row
   cursor.
   -------------------------------------------------------------------------- */

const CURSOR_ATTR = 'data-f3-cursor';
let cursorEl: HTMLElement | null = null;

function clearCursor(): void {
  cursorEl?.removeAttribute(CURSOR_ATTR);
  cursorEl = null;
}

/** Move the keyboard somewhere the console decided on, and show that it did. */
export function steerFocus(el: HTMLElement): void {
  clearCursor();
  cursorEl = el;
  el.setAttribute(CURSOR_ATTR, '');
  el.focus({ preventScroll: true });
  if (document.activeElement !== el) clearCursor();
}

/* --------------------------------------------------------------------------
   Entry motion

   §7 allows a dialog to arrive; this build let exactly one of nine do it. The
   mark is written once, on adoption, and never on something that already has
   an animation of its own — a component with an opinion about how it arrives
   keeps it, and this only fills the silence. Shared with widgets.ts, which
   marks a popover the same way.
   -------------------------------------------------------------------------- */

const ENTRY_ATTR = 'data-f3-entry';

export function markEntry(el: HTMLElement, kind: 'modal' | 'popover'): void {
  if (el.hasAttribute(ENTRY_ATTR)) return;
  /* Already moving under its own steam. Two animations on one element is one
     of them winning silently, and which one depends on stylesheet order. */
  if (getComputedStyle(el).animationName !== 'none') return;
  el.setAttribute(ENTRY_ATTR, kind);
}

/* --------------------------------------------------------------------------
   inert bookkeeping
   -------------------------------------------------------------------------- */

let inerted: HTMLElement[] = [];

function applyInert(modal: HTMLElement): void {
  releaseInert();
  const marked: HTMLElement[] = [];
  let node: HTMLElement | null = modal;
  while (node && node !== document.body) {
    const parent: HTMLElement | null = node.parentElement;
    if (!parent) break;
    for (const sib of Array.from(parent.children)) {
      if (sib === node) continue;
      if (!(sib instanceof HTMLElement)) continue;
      if (sib.hasAttribute(LAYER_ATTR)) continue;
      if (sib.inert) continue; // already inert for its own reasons — leave it
      sib.inert = true;
      marked.push(sib);
    }
    node = parent;
  }
  inerted = marked;
}

function releaseInert(): void {
  for (const el of inerted) el.inert = false;
  inerted = [];
}

/* --------------------------------------------------------------------------
   the manager
   -------------------------------------------------------------------------- */

let current: HTMLElement | null = null;
let anchor: HTMLElement | null = null;

/* The last thing focused OUTSIDE a modal.

   Reading `document.activeElement` when the dialog appears is a frame too
   late: a form dialog autofocuses its first field synchronously, so by the
   time this module sees the dialog the cursor is already inside it and there
   is nothing left to remember. Measured — open `+ New robot` from the list,
   press Esc, and focus landed on <main> rather than back on the button that
   opened it. Tracked continuously instead, which is the only way to have the
   answer before the question is asked. */
let lastOutside: HTMLElement | null = null;

/** The console's fallback focus. <body> is not a place to leave a keyboard:
    the next Tab starts again at the skip link, twelve controls from where the
    operator was. <main> resumes in the content. */
export function focusMain(): void {
  mainAnchor()?.focus({ preventScroll: true });
}

function mainAnchor(): HTMLElement | null {
  const main = document.querySelector<HTMLElement>('main');
  if (!main) return null;
  if (!main.hasAttribute('tabindex')) main.tabIndex = -1;
  return main;
}

/** `document.body` is an HTMLElement and it is always connected, so an
    unguarded anchor happily "restores" focus to a thing that cannot hold it —
    which reads to the operator as focus having been thrown away. */
function canHoldFocus(el: HTMLElement): boolean {
  if (el === document.body || el === document.documentElement) return false;
  return el.isConnected && isVisible(el);
}

function restoreFocus(): void {
  const target = anchor && canHoldFocus(anchor) ? anchor : mainAnchor();
  anchor = null;
  /* Returning the cursor to the control that opened the dialog is a steered
     move too: the operator may have opened it with the mouse, and landing back
     on an unmarked button reads as focus having been thrown away. <main> is
     never marked — a ring around the whole content area is noise, not a
     cursor. */
  if (target && target.tagName !== 'MAIN') steerFocus(target);
  else target?.focus({ preventScroll: true });
  /* focus() on something that turns out not to be focusable is silent. Land
     somewhere real rather than leaving the keyboard on <body>. */
  if (document.activeElement === document.body) mainAnchor()?.focus({ preventScroll: true });
}

function adopt(modal: HTMLElement | null): void {
  if (modal === current) return;

  if (current !== null && modal === null) {
    releaseInert();
    restoreFocus();
    current = null;
    return;
  }

  if (modal !== null) {
    const active = document.activeElement;
    if (current === null) {
      const here =
        active instanceof HTMLElement && canHoldFocus(active) && !modal.contains(active)
          ? active
          : null;
      const remembered =
        lastOutside && lastOutside.isConnected && !modal.contains(lastOutside) && canHoldFocus(lastOutside)
          ? lastOutside
          : null;
      anchor = here ?? remembered;
    }
    current = modal;
    /* Belt and braces for a dialog that was already in the DOM when this
       module installed, which the observer above never saw appear. */
    markEntry(modal, 'modal');
    applyInert(modal);
    if (active instanceof HTMLElement && modal.contains(active)) {
      /* The dialog autofocused something of its own. That is still the
         CONSOLE moving the cursor — the operator clicked a trigger — so the
         browser's `:focus-visible` heuristic sees a mouse and draws nothing.
         Same mark, same ring, as everything else this layer steers.
         Idempotent: `steerFocus` on the already-focused element fires no
         events and moves no scroll. */
      steerFocus(active);
    }
    if (!(active instanceof HTMLElement) || !modal.contains(active)) {
      /* A dialog that declares its own tabindex is saying "start on me" — that
         is the right entry for a sheet the operator READS, where landing on
         the first button would skip the content. A form dialog says nothing
         and gets its first field. */
      const declared = modal.hasAttribute('tabindex');
      const first = declared ? null : focusablesIn(modal)[0];
      if (first) steerFocus(first);
      else {
        if (!declared) modal.tabIndex = -1;
        modal.focus({ preventScroll: true });
      }
    }
  }
}

/* --------------------------------------------------------------------------
   Escape → whatever this dialog's own dismiss is
   -------------------------------------------------------------------------- */

function fire(el: Element, types: readonly string[]): void {
  for (const type of types) {
    el.dispatchEvent(
      new MouseEvent(type, { bubbles: true, cancelable: true, composed: true, view: window }),
    );
  }
}

function dismissButton(modal: HTMLElement): HTMLElement | null {
  const verbs = /^(cancel|close|dismiss|done|back)$/i;
  for (const b of Array.from(modal.querySelectorAll<HTMLElement>('button'))) {
    const name = (b.getAttribute('aria-label') ?? b.textContent ?? '').trim();
    if (verbs.test(name)) return b;
  }
  return null;
}

/** The dialog's own dismiss path, taken on the operator's behalf. Every modal
    in this build closes on its overlay; this presses that overlay rather than
    inventing a close of its own. */
function dismiss(modal: HTMLElement): void {
  if (!modal.isConnected) return;
  const scrim = modal.parentElement;
  if (scrim && scrim.getAttribute('role') === 'presentation') {
    fire(scrim, ['pointerdown', 'mousedown', 'mouseup', 'click']);
    if (!modal.isConnected) return;
  }
  window.setTimeout(() => {
    if (!modal.isConnected) return;
    dismissButton(modal)?.click();
  }, 30);
}

/* --------------------------------------------------------------------------
   install
   -------------------------------------------------------------------------- */

export function installFocusDiscipline(): () => void {
  let frame = 0;
  const sync = (): void => {
    frame = 0;
    adopt(topModal());
  };
  const schedule = (): void => {
    if (frame !== 0) return;
    frame = requestAnimationFrame(sync);
  };

  const observer = new MutationObserver(() => {
    /* Marked here rather than in `adopt` below, and the difference is one
       frame of jank: `adopt` runs on a rAF, by which point the dialog has
       already been painted at full opacity, so an entry animation added there
       would pop and then fade in from nothing. A MutationObserver callback is
       a microtask — this runs after the DOM changed and before the browser
       paints it, which is the only place an entry animation can start. */
    const modal = topModal();
    if (modal !== null && modal !== current) markEntry(modal, 'modal');
    schedule();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['role', 'aria-modal'],
  });

  function onKeyDown(e: KeyboardEvent): void {
    const modal = current;
    if (!modal || !modal.isConnected) return;

    if (e.key === 'Escape') {
      /* The dialog's own handler runs first — React's listeners are on #root,
         which is inside this bubble path. One frame later, if it is still up,
         it did not handle it and we take its scrim. */
      requestAnimationFrame(() => dismiss(modal));
      return;
    }

    if (e.key !== 'Tab') return;
    const items = focusablesIn(modal);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !modal.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Focus that falls off the end of the world. `relatedTarget: null` plus an
     activeElement of <body> one frame later means the thing that had focus was
     removed — a poll dropped the row, a panel collapsed under the cursor. */
  function onFocusOut(e: FocusEvent): void {
    if (e.relatedTarget !== null) return;
    const lost = e.target;
    requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return;
      if (lost instanceof HTMLElement && lost.isConnected) return;
      if (current) {
        focusablesIn(current)[0]?.focus({ preventScroll: true });
        return;
      }
      mainAnchor()?.focus({ preventScroll: true });
    });
  }

  function onCursorOut(e: FocusEvent): void {
    if (e.target === cursorEl) clearCursor();
  }

  function onFocusIn(e: FocusEvent): void {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.closest(MODAL_SELECTOR) !== null) return;
    if (!canHoldFocus(t)) return;
    lastOutside = t;
  }

  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('focusout', onCursorOut, true);
  document.addEventListener('focusin', onFocusIn, true);
  schedule();

  return () => {
    observer.disconnect();
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('focusout', onCursorOut, true);
    document.removeEventListener('focusin', onFocusIn, true);
    if (frame !== 0) cancelAnimationFrame(frame);
    clearCursor();
    releaseInert();
  };
}
